import Cookies from 'js-cookie/src/js.cookie';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as themeCart from '../../scripts/utils/themeCart';
import { addToCartFail, getCartSuccess, setCartActive, setCartUpdating, setMinicart } from '../store/actions/cart';
import { dispatchToast } from '../store/actions/toast';
import { RootState } from '../store/reducers';
import assignRemainingAvailablities from '../util/assignRemainingAvailablities';
import getCartVariantsAtLimit from '../util/getCartVariantsAtLimit';
import { getVoyadoSession, hasCookieConsent } from '../context/search/services/Voyado/helpers/voyadoHelpers';
import { localStorageBackedSession } from '@apptus/esales-api';
import { Cart, LineItem } from '../util/typings';

export interface ICartItem {
  id: number;
  quantity: number;
  properties?: any;
  sellingPlan?: number;
}

interface ICartUpdateByKey {
  quantity: number;
  properties?: Record<string, any>
}

export interface ICartHook {
  cart: Cart;
  items: LineItem[];
  updating: boolean;
  minicartOpen: boolean;
  openMinicart: () => void;
  closeMinicart: () => void;
  toggleMinicart: () => void;
  getCartState: () => Promise<Cart | void>;
  setDOMHelpers: () => boolean;
  updateItem: (key: string, opts: ICartUpdateByKey) => Promise<{ newCart: Cart; possibleShortStock: boolean } | void>;
  removeItem: (key: string) => Promise<Cart | void>;
  addToCart: (items: ICartItem[], notify?: boolean, callback?: (cart: Cart) => void) => Promise<Cart | void>;
  clear: () => Promise<boolean>;
  getCartAttributes: () => Promise<any>;
  updateCartAttributes: (attributes: Record<string, any>) => Promise<boolean>;
  clearCartAttributes: () => Promise<boolean>;
  getNote: () => Promise<string | null>;
  updateNote: (note: string) => Promise<boolean>;
  clearNote: () => Promise<boolean>;
  getShippingRates: () => Promise<any>;
}

export const useCart = (): ICartHook => {
  const dispatch = useDispatch()

  // selectors
  const active: boolean = useSelector((state: RootState) => state.cart.cartActive)
  const minicartOpen: boolean = useSelector((state: RootState) => state.cart.minicartOpen)
  const cart: Cart = useSelector((state: RootState) => state.cart.current)
  const updating: boolean = useSelector((state: RootState) => state.cart.updating)
  const items = (cart.items as LineItem[])

  const removeVariantUnderlayer = () => {
    const productCards = document.querySelectorAll('[data-product-card]');
    if (!productCards) return;
    productCards.forEach(productCard => {
      const productVariantHover = productCard?.querySelector('[data-open-product-variants]');
      productVariantHover?.classList.remove('open');
    })
  }

  //open minicart
  const openMinicart = () => {
    dispatch(setMinicart(true))
  }
  const closeMinicart = () => {
    dispatch(setMinicart(false));
    removeVariantUnderlayer();
  }
  const toggleMinicart = () => {
    const open: Cart = useSelector((state: RootState) => state.cart.minicartOpen)
    dispatch(setMinicart(!open))
  }


  useEffect(() => {
    // If we're on the b2b site we want the toast instead of the minicart,
    // this ensures the class doesn't get added to the body
    !window['theme'].isb2b ? document.body.classList.toggle('minicart-open', minicartOpen) : null
  }, [minicartOpen])


  useEffect(() => {
    if (items.length < 1){
      closeMinicart()
    }
  }, [items])

  const cartHasTriggerSku = (skus = [], items) => {
    if (!skus || !items) return false;

    for (const item of items) {
      if (skus.includes(item.sku)) {
        return true
      }
    }

    return false;
  }

  //does the id exist in the set of items
  const cartHasId = (id, items) => {
    if (!id || !items) return false;

    for (const item of items) {
      if (item.id === id) {
        return item.key
      }
    }

    return false;
  }

  const applyFreeGift = async (cartState) => {
    let change = false;
    for (const freeGifts of window['freeCartGifts']) {
      let hasTriggerSku = false
      let freeGiftSkuKey = null
      let hasDiscountApplied = false
      let freeGiftDiscountKey = null
      let hasReachedMinSpend = false

      switch(freeGifts.type) {
        case 'by_sku':
          hasTriggerSku = cartHasTriggerSku(freeGifts.trigger, cartState.items)
          freeGiftSkuKey  = cartHasId(freeGifts.id, cartState.items)

          if (hasTriggerSku && !freeGiftSkuKey) {
            // add the free gift
            // eslint-disable-next-line no-use-before-define
            await addToCart([{
              id: freeGifts.id,
              quantity: 1,
              properties: {'_free_gift': true}
            }])
            change = true;
          }

          if (!hasTriggerSku && freeGiftSkuKey) {
            // remove the free gift
            // eslint-disable-next-line no-use-before-define
            await removeByKey(freeGiftSkuKey)
            change = true;
            if (window.location.pathname.includes('/cart')) {
              location.reload()
            }
          }
          break;
        case 'by_discount_code':
          // If applied code at checkout and come back to cart
          // set the cookie and remove the param set during checkout
          if (localStorage.getItem('bb:checkoutDiscountCode')) {
            Cookies.set('discount_code', localStorage.getItem('bb:checkoutDiscountCode'));
            localStorage.removeItem('bb:checkoutDiscountCode')
          }
          hasDiscountApplied = Cookies.get('discount_code') === freeGifts.trigger
          freeGiftDiscountKey  = cartHasId(freeGifts.id, cartState.items)
          hasReachedMinSpend = cartState.total_price >= freeGifts.order_min

          if (hasDiscountApplied && hasReachedMinSpend && !freeGiftDiscountKey) {
            // add the free gift
            console.info('free gift added to minicart')
            //reload page after funcion is executed to trigger free gift addition to cart
            // eslint-disable-next-line no-use-before-define
            await addToCart(
              [{
                id: freeGifts.id,
                quantity: 1,
                properties: {'_free_gift': true}
              }],
              false,
              () => window.location.pathname.includes('/cart') ? location.reload() : null )
            change = true;
          }

          if ((!hasDiscountApplied || !hasReachedMinSpend) && freeGiftDiscountKey) {
            // remove the free gift
            // eslint-disable-next-line no-use-before-define
            await removeByKey(freeGiftDiscountKey)
            change = true;
            if (window.location.pathname.includes('/cart')) {
              location.reload()
            }
          }
          break;
      }
    }
    return change;
  }

  // Helper to update Voyado cart attributes (for checkout extension)
  const updateVoyadoCartAttributes = async (currentAttributes: any) => {
    try {
      // Only update if Voyado is the active search service
      if (window['blubolt']?.SearchService !== 'voyado') {
        return;
      }

      const session = getVoyadoSession(localStorageBackedSession);
      const sessionKeys = session();
      const hasConsent = hasCookieConsent();

      // Only update if changed (avoid unnecessary API calls)
      const needsUpdate =
        currentAttributes._voyado_customer_key !== sessionKeys.customerKey ||
        currentAttributes._voyado_session_key !== sessionKeys.sessionKey ||
        currentAttributes._voyado_consent !== (hasConsent ? 'true' : 'false');

      if (needsUpdate) {
        await themeCart.updateAttributes({
          '_voyado_customer_key': sessionKeys.customerKey,
          '_voyado_session_key': sessionKeys.sessionKey,
          '_voyado_consent': hasConsent ? 'true' : 'false'
        });
      }
    } catch (error) {
      console.warn('Failed to update Voyado cart attributes:', error);
      // Don't block cart operation if this fails
    }
  };

  const getCartState = async (): Promise<Cart|void>   => {
    try {
      const [shopifyCartState, variantsAtLimit] = await Promise.all([
        themeCart.getState(),
        getCartVariantsAtLimit()
      ]);
      const cartState = assignRemainingAvailablities(shopifyCartState, variantsAtLimit)
      if (cartState) {
        // Update Voyado cart attributes for checkout extension
        await updateVoyadoCartAttributes(cartState.attributes);

        const freeGiftChange = await applyFreeGift(cartState)
        if (freeGiftChange) {
          return await getCartState()
        }
        dispatch(getCartSuccess(cartState))
        if (!active) {
          dispatch(setCartActive(true))
        }
        return cartState;
      }
      dispatch(setCartActive(false))
    } catch (e) {
      console.error(e);
      dispatch(setCartActive(false))
    }
  }


  /**
   * Adds an Item / Items to cart
   *
   * @param items the items to add to the cart
   * @param notify whether to display the minicart or not
   * @param callback completion handler
   */
  const addToCart = async (items: ICartItem[], notify = true, callback?: (cart: Cart) => void) : Promise<Cart|void> => {
    const itemsAddedArray = [];
    const quantityArray = [];
    dispatch(setCartUpdating(true))
    for (const item of items) {
      const { id, quantity, properties, sellingPlan } = item;
      try {
        const itemAdded = await themeCart.addItem(id, { quantity, properties, sellingPlan })
        if (itemAdded && itemAdded.id) {
          itemsAddedArray.push(itemAdded.id)
          quantityArray.push(quantity)
          //Could dispatch an event here, if used for tracking...
          window.dispatchEvent(new CustomEvent('blubolt:add-to-cart', { detail: { itemAdded: itemAdded } }))
        }
      } catch (error) {
        //you probably want to disable adding more than available at the liquid/component level, but for other cases:
        dispatch(addToCartFail(error));
        const err = await error.json()
        dispatch(dispatchToast('error', { customMessage: err.description }));
      }
    }

    const newCart = await getCartState();
    if (!newCart) {
      return
    }

    if (itemsAddedArray.length > 0) {
      if (notify) {
        window['theme'].isb2b ? dispatch(dispatchToast('cart', itemsAddedArray)) : window['blubolt'].minicart ? openMinicart() : dispatch(dispatchToast('cart', itemsAddedArray, quantityArray));
      }
      if (callback) {
        callback(newCart);
      }
    }

    dispatch(setCartUpdating(false))

    return newCart;
  }


  // Update one item
  const updateCartByKey = async (key: string, opts: ICartUpdateByKey) => {

    if (!key || !opts) { return console.error('A key or options are required to update the cart') }

    dispatch(setCartUpdating(true))
    let possibleShortStock = false
    try {
      let checkKey = key;
      let originalKeys = []
      if (opts.properties) {
        //if we're chagning properties, we may get a new key
        const originalCart = await getCartState();
        if (!originalCart) {
          return
        }
        originalKeys = originalCart.items.map((cartItem) => cartItem.key)
      }
      await themeCart.updateItem(key, opts)
      const newCart = await getCartState()
      if (!newCart) {
        return
      }
      if (opts.properties) {
        const newKeys = newCart.items.map((cartItem) => cartItem.key)
        const newKey = newKeys.find(iKey => !originalKeys.includes(iKey));
        checkKey = newKey ? key : newKey;
      }
      const updatedItem = newCart.items.find(item => item.key === checkKey)
      if (opts.quantity) {
        // best effort stock availability response
        if (updatedItem.quantity < opts.quantity) {
          possibleShortStock = true
          dispatch(dispatchToast('error', { customMessage: `Cannot add more than ${updatedItem.quantity} of ${updatedItem.title}` }));
        }
      }
      dispatch(setCartUpdating(false))
      return { newCart, possibleShortStock }

    } catch (error) {
      console.error('Error updating cart:', error.message)
      dispatch(setCartUpdating(false))
    }

  }

  // Remove one item
  const removeByKey = async (key) => {
    dispatch(setCartUpdating(true))
    if (!key) { return console.error('A key is required to remove item from the cart') }
    try {
      await themeCart.removeItem(key)
      const newCart = await getCartState()
      dispatch(setCartUpdating(false))
      return newCart

    } catch (error) {
      console.error('Error removing item from cart:', error.message)
      dispatch(setCartUpdating(false))
    }
  }

  // clear the items out of the cart
  const clearCart = async () => {
    try {
      await themeCart.clearItems()
      return true
    } catch (error) {
      console.error('Error clearing cart items cart:', error.message)
      return false
    }
  }

  const setDOMHelpers = () => {
    window['blubolt'].cart = {
      currentCart: cart,
      getCartState,
      refresh: getCartState,
      addToCart,
      updateItem: updateCartByKey,
      removeItem: removeByKey,
      clear: clearCart
    };
    window['blubolt'].minicart = {
      clickOpen: (e: Event) => {
        if (!cart.item_count) {
          return true
        }
        if (!window['theme'].isb2b) {
          e.preventDefault()
          return openMinicart()
        }
      },
      open: openMinicart,
      close: closeMinicart,
      toggle: toggleMinicart
    }
    return true
  }

  return {
    cart,
    items,
    updating,
    minicartOpen,
    openMinicart,
    closeMinicart,
    toggleMinicart,
    getCartState,
    setDOMHelpers,
    updateItem: updateCartByKey,
    removeItem: removeByKey,
    addToCart,
    clear: clearCart,
    getCartAttributes: themeCart.getAttributes,
    updateCartAttributes: themeCart.updateAttributes,
    clearCartAttributes: themeCart.clearAttributes,
    getNote: themeCart.getNote,
    updateNote: themeCart.updateNote,
    clearNote: themeCart.clearNote,
    getShippingRates: themeCart.getShippingRates,
  }
}
